import { Controller } from "@hotwired/stimulus"

// Простой WYSIWYG для текстового поля.
// Связывает hidden textarea с contenteditable-областью и панелью кнопок.
// Загрузка картинок — POST /admin/uploads, вставка <img src="/uploads/…">
// в позицию каретки. По клику на картинку всплывает мини-тулбар с кнопками
// «уже / шире / описание / удалить» — без диалогов prompt.
export default class extends Controller {
  static targets = ["editor", "input"]

  static WIDTH_STEPS = [25, 33, 50, 66, 75, 100]

  connect() {
    this.editorTarget.innerHTML = this.inputTarget.value || ""
    this.element.querySelectorAll("[data-wysiwyg-cmd]").forEach((el) => {
      if (el.tagName === "SELECT") {
        el.addEventListener("change", (e) => this.execFromSelect(e))
      } else {
        el.addEventListener("mousedown", (e) => e.preventDefault())
        el.addEventListener("click", (e) => this.exec(e))
      }
    })
    this.editorTarget.addEventListener("input", () => this.sync())
    this.editorTarget.addEventListener("blur", () => {
      this.savedRange = this.captureRange()
      this.sync()
    })
    this.editorTarget.addEventListener("paste", (e) => this.handlePaste(e))
    this.editorTarget.addEventListener("click", (e) => this.handleClick(e))
    this.element.closest("form")?.addEventListener("submit", () => {
      // Если форму сабмитят в HTML-режиме — забираем актуальный текст из textarea.
      if (this.htmlMode) this.applyHtmlFromTextarea()
      this.sync()
    })

    this.buildHtmlArea()

    this.fileInput = document.createElement("input")
    this.fileInput.type = "file"
    this.fileInput.accept = "image/*"
    this.fileInput.style.display = "none"
    this.fileInput.addEventListener("change", () => this.handleFile())
    this.element.appendChild(this.fileInput)

    this.buildImageToolbar()
    this.onDocClick = (e) => this.handleDocClick(e)
    this.onReposition = () => this.positionImageToolbar()
    document.addEventListener("click", this.onDocClick, true)
    window.addEventListener("scroll", this.onReposition, true)
    window.addEventListener("resize", this.onReposition)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocClick, true)
    window.removeEventListener("scroll", this.onReposition, true)
    window.removeEventListener("resize", this.onReposition)
    this.imageToolbar?.remove()
  }

  exec(event) {
    const btn = event.currentTarget
    const cmd = btn.dataset.wysiwygCmd
    const value = btn.dataset.wysiwygValue

    this.editorTarget.focus()

    if (cmd === "createLink") {
      this.savedRange = this.captureRange()
      this.savedSelectionText = window.getSelection()?.toString() || ""
      this.openLinkDialog()
      return
    } else if (cmd === "removeFormat") {
      document.execCommand("removeFormat", false, null)
      document.execCommand("unlink", false, null)
    } else if (cmd === "insertImage") {
      this.savedRange = this.captureRange()
      this.fileInput.value = ""
      this.fileInput.click()
    } else if (cmd === "insertHTML") {
      this.savedRange = this.captureRange()
      this.openInsertHtmlDialog()
    } else if (cmd === "toggleHtml") {
      this.toggleHtmlMode()
    } else if (cmd === "fontSize" || cmd === "fontName") {
      document.execCommand("styleWithCSS", false, true)
      document.execCommand(cmd, false, value)
      document.execCommand("styleWithCSS", false, false)
    } else {
      document.execCommand(cmd, false, value || null)
    }

    this.sync()
  }

  execFromSelect(event) {
    const sel = event.currentTarget
    const cmd = sel.dataset.wysiwygCmd
    const value = sel.value
    if (!value) return

    this.editorTarget.focus()
    if (this.savedRange) {
      const s = window.getSelection()
      s.removeAllRanges()
      s.addRange(this.savedRange)
    }

    if (cmd === "fontSize" || cmd === "fontName") {
      document.execCommand("styleWithCSS", false, true)
      document.execCommand(cmd, false, value)
      document.execCommand("styleWithCSS", false, false)
    } else {
      document.execCommand(cmd, false, value)
    }

    sel.value = ""
    this.sync()
  }

  handlePaste(event) {
    event.preventDefault()
    const text = (event.clipboardData || window.clipboardData).getData("text/plain")
    document.execCommand("insertText", false, text)
  }

  handleClick(event) {
    if (event.target.tagName !== "IMG") {
      this.hideImageToolbar()
      return
    }
    event.preventDefault()
    this.selectImage(event.target)
  }

  // Скрываем тулбар, если клик не по картинке и не по самому тулбару.
  handleDocClick(event) {
    if (!this.activeImage) return
    if (event.target === this.activeImage) return
    if (this.imageToolbar?.contains(event.target)) return
    this.hideImageToolbar()
  }

  captureRange() {
    const sel = window.getSelection()
    if (sel.rangeCount === 0) return null
    const range = sel.getRangeAt(0)
    return this.editorTarget.contains(range.startContainer) ? range.cloneRange() : null
  }

  async handleFile() {
    const file = this.fileInput.files?.[0]
    if (!file) return

    const fd = new FormData()
    fd.append("file", file)

    const token = document.querySelector("meta[name='csrf-token']")?.content
    let res
    try {
      res = await fetch("/admin/uploads", {
        method: "POST",
        body: fd,
        headers: token ? { "X-CSRF-Token": token } : {}
      })
    } catch (e) {
      alert("Ошибка сети при загрузке: " + e.message)
      return
    }

    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      alert("Не удалось загрузить: " + (data.error || res.status))
      return
    }

    const { url } = await res.json()
    this.insertImage(url, file.name)
  }

  insertImage(url, alt) {
    this.editorTarget.focus()
    if (this.savedRange) {
      const sel = window.getSelection()
      sel.removeAllRanges()
      sel.addRange(this.savedRange)
    }
    const safeAlt = (alt || "").replace(/"/g, "&quot;")
    const html = `<img src="${url}" alt="${safeAlt}" class="cms-img">`
    document.execCommand("insertHTML", false, html)
    this.sync()
  }

  sync() {
    this.inputTarget.value = this.editorTarget.innerHTML.trim()
  }

  // ───── HTML-режим: textarea с исходным кодом ─────────────────────────

  buildHtmlArea() {
    const ta = document.createElement("textarea")
    ta.className = "wysiwyg-html-area"
    ta.style.display = "none"
    ta.spellcheck = false
    ta.addEventListener("input", () => this.applyHtmlFromTextarea())
    this.editorTarget.insertAdjacentElement("afterend", ta)
    this.htmlArea = ta
  }

  toggleHtmlMode() {
    if (this.htmlMode) {
      this.applyHtmlFromTextarea()
      this.htmlArea.style.display = "none"
      this.editorTarget.style.display = ""
      this.htmlMode = false
    } else {
      this.htmlArea.value = this.formatHtml(this.editorTarget.innerHTML)
      this.htmlArea.style.display = "block"
      this.editorTarget.style.display = "none"
      this.htmlMode = true
      // Высота textarea подгоняется к редактору.
      const h = Math.max(220, this.editorTarget.offsetHeight)
      this.htmlArea.style.minHeight = `${h}px`
    }
    this.updateToggleButton()
    this.hideImageToolbar()
  }

  applyHtmlFromTextarea() {
    this.editorTarget.innerHTML = this.htmlArea.value
    this.sync()
  }

  updateToggleButton() {
    const btn = this.element.querySelector('[data-wysiwyg-cmd="toggleHtml"]')
    if (!btn) return
    btn.classList.toggle("is-active", !!this.htmlMode)
    btn.textContent = this.htmlMode ? "📝 текст" : "</> HTML"
  }

  // Лёгкое форматирование: переносы строк перед открывающими блочными тегами.
  formatHtml(src) {
    const blocks = "h1|h2|h3|h4|p|ul|ol|li|div|figure|figcaption|blockquote|hr|img"
    return src
      .replace(new RegExp(`<(${blocks})(\\b[^>]*)?>`, "g"), "\n<$1$2>")
      .replace(new RegExp(`</(${blocks})>`, "g"), "</$1>\n")
      .replace(/\n{2,}/g, "\n")
      .trim()
  }

  // ───── Диалог «Вставить ссылку» ──────────────────────────────────────

  openLinkDialog() {
    if (this.linkDialog) { this.linkDialog.remove(); this.linkDialog = null }

    const overlay = document.createElement("div")
    overlay.className = "wysiwyg-html-dialog"
    overlay.innerHTML = `
      <div class="wysiwyg-html-dialog__box">
        <div class="wysiwyg-html-dialog__title">Вставить ссылку</div>
        <label class="wysiwyg-link-dialog__label">URL</label>
        <input type="url" class="wysiwyg-link-dialog__input" data-role="url" placeholder="https://example.com" value="https://">
        <label class="wysiwyg-link-dialog__label">Текст ссылки</label>
        <input type="text" class="wysiwyg-link-dialog__input" data-role="text" placeholder="Текст, который увидит посетитель">
        <div class="wysiwyg-link-dialog__targets">
          <label><input type="radio" name="wysiwyg-link-target" value="self" checked> В этой же вкладке</label>
          <label><input type="radio" name="wysiwyg-link-target" value="blank"> В новой вкладке</label>
        </div>
        <div class="wysiwyg-html-dialog__actions">
          <button type="button" data-act="cancel">Отмена</button>
          <button type="button" data-act="insert" class="wysiwyg-html-dialog__primary">Вставить</button>
        </div>
      </div>
    `
    document.body.appendChild(overlay)
    this.linkDialog = overlay

    const urlInput = overlay.querySelector('[data-role="url"]')
    const textInput = overlay.querySelector('[data-role="text"]')
    textInput.value = this.savedSelectionText || ""
    setTimeout(() => urlInput.focus(), 0)

    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) this.closeLinkDialog()
    })
    overlay.querySelector('[data-act="cancel"]').addEventListener("click", () => this.closeLinkDialog())
    overlay.querySelector('[data-act="insert"]').addEventListener("click", () => this.submitLinkDialog())
    overlay.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.closeLinkDialog()
      if (e.key === "Enter" && e.target.tagName !== "BUTTON") {
        e.preventDefault()
        this.submitLinkDialog()
      }
    })
  }

  closeLinkDialog() {
    this.linkDialog?.remove()
    this.linkDialog = null
  }

  submitLinkDialog() {
    if (!this.linkDialog) return
    const url = this.linkDialog.querySelector('[data-role="url"]').value.trim()
    if (!url || url === "https://") return this.closeLinkDialog()
    const text = this.linkDialog.querySelector('[data-role="text"]').value.trim()
    const newTab = this.linkDialog.querySelector('input[name="wysiwyg-link-target"]:checked')?.value === "blank"
    this.closeLinkDialog()
    this.insertLink(url, text, newTab)
  }

  insertLink(url, text, newTab) {
    this.editorTarget.focus()
    if (this.savedRange) {
      const sel = window.getSelection()
      sel.removeAllRanges()
      sel.addRange(this.savedRange)
    }
    const safeUrl = url.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    const safeText = (text || url).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    const attrs = newTab ? ` target="_blank" rel="noopener noreferrer"` : ""
    document.execCommand("insertHTML", false, `<a href="${safeUrl}"${attrs}>${safeText}</a>`)
    this.sync()
  }

  // ───── Диалог «Вставить HTML» ───────────────────────────────────────

  openInsertHtmlDialog() {
    if (this.htmlDialog) { this.htmlDialog.remove(); this.htmlDialog = null }

    const overlay = document.createElement("div")
    overlay.className = "wysiwyg-html-dialog"
    overlay.innerHTML = `
      <div class="wysiwyg-html-dialog__box">
        <div class="wysiwyg-html-dialog__title">Вставить HTML</div>
        <textarea class="wysiwyg-html-dialog__input" spellcheck="false"
                  placeholder="<div class='abrin-partners__card'>…</div>"></textarea>
        <div class="wysiwyg-html-dialog__hint">Фрагмент будет вставлен в место курсора. Разрешённые теги: h2/h3, p, ul/ol/li, b/strong/em/u, a, br, hr, blockquote, div, span, img.</div>
        <div class="wysiwyg-html-dialog__actions">
          <button type="button" data-act="cancel">Отмена</button>
          <button type="button" data-act="insert" class="wysiwyg-html-dialog__primary">Вставить</button>
        </div>
      </div>
    `
    document.body.appendChild(overlay)
    this.htmlDialog = overlay

    const ta = overlay.querySelector("textarea")
    setTimeout(() => ta.focus(), 0)

    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) this.closeInsertHtmlDialog()
    })
    overlay.querySelector('[data-act="cancel"]').addEventListener("click", () => this.closeInsertHtmlDialog())
    overlay.querySelector('[data-act="insert"]').addEventListener("click", () => {
      const html = ta.value.trim()
      if (!html) return this.closeInsertHtmlDialog()
      this.insertHtmlAtCaret(html)
      this.closeInsertHtmlDialog()
    })
    overlay.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.closeInsertHtmlDialog()
    })
  }

  closeInsertHtmlDialog() {
    this.htmlDialog?.remove()
    this.htmlDialog = null
  }

  insertHtmlAtCaret(html) {
    if (this.htmlMode) {
      // В HTML-режиме вставляем прямо в textarea на позицию курсора.
      const ta = this.htmlArea
      const start = ta.selectionStart || 0
      const end = ta.selectionEnd || 0
      ta.value = ta.value.slice(0, start) + html + ta.value.slice(end)
      ta.selectionStart = ta.selectionEnd = start + html.length
      ta.focus()
      this.applyHtmlFromTextarea()
      return
    }
    this.editorTarget.focus()
    if (this.savedRange) {
      const sel = window.getSelection()
      sel.removeAllRanges()
      sel.addRange(this.savedRange)
    }
    document.execCommand("insertHTML", false, html)
    this.sync()
  }

  // ───── Floating toolbar over the selected image ──────────────────────

  buildImageToolbar() {
    const bar = document.createElement("div")
    bar.className = "wysiwyg-image-toolbar"
    bar.setAttribute("role", "toolbar")
    bar.innerHTML = `
      <button type="button" data-act="shrink" title="Уже">−</button>
      <span class="wysiwyg-image-toolbar__width" data-role="width">100%</span>
      <button type="button" data-act="grow" title="Шире">+</button>
      <span class="wysiwyg-image-toolbar__sep"></span>
      <button type="button" data-act="alt" title="Описание (alt)">Описание</button>
      <button type="button" data-act="delete" title="Удалить" class="wysiwyg-image-toolbar__danger">Удалить</button>
    `
    bar.style.display = "none"
    document.body.appendChild(bar)

    bar.querySelectorAll("button[data-act]").forEach((btn) => {
      btn.addEventListener("mousedown", (e) => e.preventDefault())
      btn.addEventListener("click", (e) => {
        e.preventDefault()
        this.handleImageAction(btn.dataset.act)
      })
    })

    this.imageToolbar = bar
  }

  selectImage(img) {
    this.activeImage = img
    img.classList.add("is-selected")
    this.imageToolbar.style.display = "inline-flex"
    this.updateWidthLabel()
    this.positionImageToolbar()
  }

  hideImageToolbar() {
    if (this.activeImage) this.activeImage.classList.remove("is-selected")
    this.activeImage = null
    if (this.imageToolbar) this.imageToolbar.style.display = "none"
  }

  positionImageToolbar() {
    if (!this.activeImage || !this.imageToolbar) return
    const rect = this.activeImage.getBoundingClientRect()
    const tbar = this.imageToolbar
    const tbarH = tbar.offsetHeight || 36
    const tbarW = tbar.offsetWidth || 240
    const top = window.scrollY + rect.top - tbarH - 8
    const left = window.scrollX + rect.left + (rect.width - tbarW) / 2
    tbar.style.top = `${Math.max(8, top)}px`
    tbar.style.left = `${Math.max(8, left)}px`
  }

  currentWidthStep() {
    const cls = this.activeImage?.className || ""
    const m = cls.match(/cms-img--w(\d+)/)
    return m ? parseInt(m[1], 10) : 100
  }

  setWidthStep(percent) {
    if (!this.activeImage) return
    this.activeImage.className = `cms-img cms-img--w${percent} is-selected`
    this.updateWidthLabel()
    this.positionImageToolbar()
    this.sync()
  }

  updateWidthLabel() {
    const el = this.imageToolbar.querySelector('[data-role="width"]')
    if (el) el.textContent = `${this.currentWidthStep()}%`
  }

  handleImageAction(act) {
    if (!this.activeImage) return
    const steps = this.constructor.WIDTH_STEPS
    const current = this.currentWidthStep()
    const idx = steps.indexOf(current)

    if (act === "shrink") {
      const next = steps[Math.max(0, (idx === -1 ? steps.length - 1 : idx) - 1)]
      this.setWidthStep(next)
    } else if (act === "grow") {
      const next = steps[Math.min(steps.length - 1, (idx === -1 ? steps.length - 1 : idx) + 1)]
      this.setWidthStep(next)
    } else if (act === "alt") {
      const alt = window.prompt("Описание картинки (alt):", this.activeImage.alt || "")
      if (alt !== null) {
        this.activeImage.alt = alt
        this.sync()
      }
    } else if (act === "delete") {
      const img = this.activeImage
      this.hideImageToolbar()
      img.remove()
      this.sync()
    }
  }
}
